import dynamic from "next/dynamic";
import PropTypes from "prop-types";
import { useState, useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";
import Modal from "react-modal";

import colors from "constants/colors";
import { useMediaQueryContext } from "lib/mediaQueryContext";

import IconSearch from "components/shared/svg/IconSearch";
import IconCheck from "components/shared/svg/IconCheck";
import MyLocation from "./myLocation/MyLocation";

import {
    AddressInputWrapper,
    InputAddress,
    LocationContainer,
    IconSearchWrapper
} from "./AddressInput.styled";

import AddressSuggestions from "./addressSuggestions/AddressSuggestions";

const AddressForm = dynamic(() => import("./addressForm/AddressForm"));

function isAddressValid(addressFields, cityOnly) {
    const requiredFields = [
        "street",
        "houseNumber",
        "postalCode",
        "city",
        "country"
    ];

    if (cityOnly) {
        return true;
    }

    return requiredFields.every(
        field => addressFields[field] && addressFields[field] !== ""
    );
}

function filterAddressFields(addressFields, cityOnly) {
    let result = {};
    const keys = Object.keys(addressFields);
    const cityOnlyFields = ["postalCode", "city", "country"];

    if (cityOnly) {
        keys.forEach(key => {
            if (cityOnlyFields.includes(key)) {
                result[key] = addressFields[key];
            }
        });
    } else {
        result = addressFields;
    }

    return result;
}

function addressToString(fields, cityOnly) {
    let addressString = "";

    if (fields?.street) {
        addressString += `${fields.street}`;
    }
    if (fields?.houseNumber) {
        addressString += ` ${fields.houseNumber}`;
    }
    if (fields?.suffix) {
        addressString += ` ${fields.suffix}`;
    }
    if (fields?.postalCode !== "") {
        addressString += cityOnly
            ? fields.postalCode
            : `, ${fields.postalCode}`;
    }
    if (fields?.city) {
        addressString +=
            cityOnly && fields.postalCode === ""
                ? fields.city
                : `, ${fields.city}`;
    }
    if (fields?.country) {
        addressString += `, ${fields.country}`;
    }

    return addressString;
}

function AddressInput({ name, label, options }) {
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [suggestionIsOpen, setSuggestionIsOpen] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [partialAddress, setPartialAddres] = useState();
    const [lastInput, setLastInput] = useState("");
    const [addressFields, setAddressFields] = useState({});
    const { mobileView } = useMediaQueryContext();

    const [fieldActive, setFieldActive] = useState(false);
    const {
        register,
        unregister,
        formState,
        watch,
        setValue
    } = useFormContext();
    const addressInputValue = watch(`${name}Address`);
    const { placeholder, required, onBlur, onClick, cityOnly } = options;
    const blurRef = useRef(onBlur);

    // Create object in hook form for address.
    useEffect(() => {
        register({ name: `${name}.city` }, { required: true });

        if (!cityOnly) {
            register({ name: `${name}.country` }, { required: true });
            register({ name: `${name}.street` }, { required: true });
            register(
                { name: `${name}.houseNumber` },
                {
                    required: true,
                    pattern: {
                        value: /^\d+$/,
                        message: "error message"
                    }
                }
            );
            register({ name: `${name}.postalCode` }, { required: true });
            register({ name: `${name}.suffix` });
        }

        return () => {
            unregister(`${name}.city`);
            if (!cityOnly) {
                unregister(`${name}.country`);
                unregister(`${name}.street`);
                unregister(`${name}.houseNumber`);
                unregister(`${name}.postalCode`);
                unregister(`${name}.suffix`);
            }
        };
    }, [register, unregister, name, cityOnly]);

    // Set form values when address is selected
    useEffect(() => {
        const address = filterAddressFields(addressFields, cityOnly);
        const formattedAddress = addressToString(addressFields, cityOnly);

        if (Object.keys(address).length > 0) {
            setLastInput(formattedAddress);

            setValue(name, address, {
                shouldDirty: true,
                shouldValidate: true
            });

            setValue(`${name}Address`, formattedAddress, {
                shouldDirty: true,
                shouldValidate: true
            });

            if (blurRef.current) {
                blurRef.current();
            }
        }
    }, [addressFields, cityOnly, name, setValue]);

    // Clear address data if input changes
    useEffect(() => {
        if (lastInput !== addressInputValue && lastInput !== "") {
            setLastInput(addressInputValue);
            setAddressFields({});
        }
    }, [addressInputValue, lastInput]);

    // Close modal for non-mobile devices
    useEffect(() => {
        if (!mobileView) {
            setModalIsOpen(false);
        }
    }, [mobileView]);

    // Action to openModal
    const onOpenModal = data => {
        const partialAddressData = data ?? null;
        setPartialAddres(partialAddressData);
        setModalIsOpen(true);
    };

    // Handle showing suggestions for MyLocation Component
    function handleSetSuggestionsFromLocation(data) {
        setSuggestions(data);

        if (data.length > 0) {
            setSuggestionIsOpen(true);
        }
    }

    // Handle setting shipping address object and string in form
    function handleSetShipping(data) {
        if (isAddressValid(data, cityOnly)) {
            setAddressFields(filterAddressFields(data, cityOnly));
        }

        if (suggestionIsOpen) {
            setSuggestionIsOpen(false);
        }

        if (modalIsOpen) {
            setModalIsOpen(false);
        }
    }

    // Hide suggestions on blur
    function handleBur() {
        if (blurRef.current) {
            blurRef.current();
        }

        setFieldActive(false);

        setTimeout(() => {
            setSuggestionIsOpen(false);
        }, 300);
    }

    return (
        <AddressInputWrapper>
            <LocationContainer>
                <MyLocation
                    name={`${name}Address`}
                    setSuggestions={data =>
                        handleSetSuggestionsFromLocation(data)
                    }
                />
            </LocationContainer>

            <InputAddress
                label={label}
                name={`${name}Address`}
                leftIcon={
                    <IconSearchWrapper fieldActive={fieldActive}>
                        <IconSearch fill="var(--color-lightNight)" />
                    </IconSearchWrapper>
                }
                placeholder={placeholder}
                maxLength="100"
                autoComplete="cc-number"
                onClick={onClick}
                onBlur={() => handleBur()}
                onFocus={() => {
                    setSuggestionIsOpen(true);
                    setFieldActive(true);
                }}
                ref={register({
                    required: required && `${label} is verplicht`,
                    validate: () => {
                        if (!isAddressValid(addressFields, cityOnly)) {
                            return "Het adres is onvolledig. Kies een adres of voer jouw adres handmatig in.";
                        }

                        return true;
                    },
                    shouldUnregister: true
                })}
                errorMessage={formState.errors?.[`${name}Address`]?.message}
                rightIcon={
                    addressInputValue &&
                    !formState.errors?.[`${name}Address`] && (
                        <IconCheck fill={colors.green} />
                    )
                }
                suggestionIsOpen={suggestionIsOpen}
            />

            <AddressSuggestions
                name={name}
                input={addressInputValue}
                setAddress={handleSetShipping}
                suggestionIsOpen={suggestionIsOpen}
                setSuggestionIsOpen={setSuggestionIsOpen}
                suggestions={suggestions}
                setSuggestions={setSuggestions}
                toggleModal={data => onOpenModal(data)}
                cityOnly={cityOnly}
                instructions={options?.instructions}
            />
            {!cityOnly && (
                <Modal
                    isOpen={modalIsOpen}
                    contentLabel={`${name}Modal`}
                    onRequestClose={() => setModalIsOpen(false)}
                    ariaHideApp={false}
                    className="react-modal"
                    closeTimeoutMS={400}
                    overlayClassName="react-modal-overlay"
                >
                    <AddressForm
                        address={partialAddress}
                        setAddress={handleSetShipping}
                        closeModal={() => setModalIsOpen(false)}
                    />
                </Modal>
            )}
        </AddressInputWrapper>
    );
}

AddressInput.propTypes = {
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    options: PropTypes.shape({
        placeholder: PropTypes.string,
        required: PropTypes.bool,
        onBlur: PropTypes.func,
        onClick: PropTypes.func,
        cityOnly: PropTypes.bool,
        instructions: PropTypes.string
    })
};

AddressInput.defaultProps = {
    options: {
        placeholder: "Vul hier je bezorgadres in",
        required: true,
        onBlur: () => {},
        onClick: () => {},
        cityOnly: false
    }
};

export default AddressInput;
